export * from './types';

// Utility functions

/**
 * Generate a random room code (6 characters, uppercase letters and numbers)
 */
export function generateRoomCode(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Removed confusing chars like 0, O, 1, I
  let code = '';
  for (let i = 0; i < 6; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
}

/**
 * Generate a UUID v4
 */
export function generateId(): string {
  return crypto.randomUUID();
}

/**
 * Format score with commas
 */
export function formatScore(score: number): string {
  return score.toLocaleString();
}

/**
 * Get ordinal suffix for a number (1st, 2nd, 3rd, etc.)
 */
export function getOrdinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

/**
 * Shuffle an array (Fisher-Yates algorithm)
 */
export function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

/**
 * Pick random item from array
 */
export function pickRandom<T>(array: T[]): T {
  return array[Math.floor(Math.random() * array.length)];
}

/**
 * Pick random items from array (without repetition)
 */
export function pickRandomMultiple<T>(array: T[], count: number): T[] {
  return shuffleArray(array).slice(0, count);
}

/**
 * Validate room code format
 */
export function isValidRoomCode(code: string): boolean {
  return /^[A-Z0-9]{6}$/.test(code);
}

/**
 * Sanitize player name
 */
export function sanitizePlayerName(name: string): string {
  return name.trim().slice(0, 20).replace(/[<>]/g, '');
}

// Arabic/English word lists for Spy game
export const SPY_WORDS = {
  places: [
    'مطار', 'مستشفى', 'مدرسة', 'مطعم', 'فندق', 'شاطئ', 'جبل', 'مسجد', 'كنيسة', 'سينما',
    'ملعب', 'حديقة', 'مكتبة', 'متحف', 'سوبرماركت', 'بنك', 'صيدلية', 'محطة قطار', 'مول', 'جامعة'
  ],
  food: [
    'بيتزا', 'برجر', 'شاورما', 'كشري', 'فول', 'طعمية', 'كبسة', 'منسف', 'مشاوي', 'سوشي',
    'باستا', 'فلافل', 'حمص', 'تبولة', 'كنافة', 'بقلاوة', 'آيس كريم', 'شوكولاتة', 'كيك', 'دونات'
  ],
  animals: [
    'أسد', 'فيل', 'قطة', 'كلب', 'حصان', 'جمل', 'زرافة', 'قرد', 'دب', 'نمر',
    'ثعلب', 'أرنب', 'غزال', 'ذئب', 'نسر', 'صقر', 'بطريق', 'دولفين', 'حوت', 'سلحفاة'
  ],
  jobs: [
    'طبيب', 'مهندس', 'معلم', 'طيار', 'شرطي', 'إطفائي', 'طباخ', 'ممرض', 'محامي', 'صحفي',
    'مصور', 'رسام', 'موسيقي', 'ممثل', 'مزارع', 'سائق', 'خباز', 'حلاق', 'نجار', 'كهربائي'
  ],
  sports: [
    'كرة قدم', 'كرة سلة', 'تنس', 'سباحة', 'جري', 'ملاكمة', 'كاراتيه', 'جمباز', 'تزلج', 'ركوب خيل',
    'كرة طائرة', 'بيسبول', 'غولف', 'بولينج', 'رماية', 'مصارعة', 'رفع أثقال', 'دراجات', 'تسلق', 'غوص'
  ]
};

// Quiz categories
export const QUIZ_CATEGORIES = [
  'معلومات عامة',
  'رياضة',
  'تاريخ',
  'جغرافيا',
  'علوم',
  'فن وثقافة',
  'أفلام ومسلسلات',
  'موسيقى',
  'تكنولوجيا',
  'طعام'
];
